import React, { ChangeEvent, Fragment } from "react";
import { RouteComponentProps, Prompt } from "react-router";

import { DateTime } from "luxon";

import { V2, apiGet } from "../../Api";
import { formatName } from "../../Utilities";
import TabControl from "../TabControl";
import withTitle from "../withTitle";
import { WellKnownGroups } from "../AuthContext";

interface IMetricsCaseStatsGoalEditViewState {
    isChanged: boolean;
    isGoalsChanged: boolean;
    isColorsChanged: boolean;
    isLoading: boolean | string;
    isSubmitting: boolean | string;

    providers: any[];
    locations: any[];
    practices: any[];
    goals: { [name: string]: any };
    employeeGoals: any;
    locationGoals: any;
    practiceGoals: any;
    companyGoals: any;

    newEmployee: string;

    colorValueYellow: string;
    colorValueDullGreen: string;
    colorValueGreen: string;

    [name: string]: any;
}

const TOTALS_CPT = "T0000";

class MetricsCaseStatsGoalEditView extends React.Component<RouteComponentProps<any>, IMetricsCaseStatsGoalEditViewState> {
    private cpt_list = ["58558", "58563", "57288", "58570", "58660", TOTALS_CPT];

    constructor(props: Readonly<RouteComponentProps<any>>) {
        super(props);

        const state: IMetricsCaseStatsGoalEditViewState = {
            isChanged: false,
            isColorsChanged: false,
            isGoalsChanged: false,
            isLoading: true,
            isSubmitting: false,

            employeeGoals: {},
            locationGoals: {},
            practiceGoals: {},
            companyGoals: {},
            goals: {},
            providers: [],
            locations: [],
            practices: [],

            newEmployee: "",

            colorValueYellow: "",
            colorValueDullGreen: "",
            colorValueGreen: "",
        };

        this.state = state;

        this.handleEmployeeTableChanged = this.handleEmployeeTableChanged.bind(this);
        this.addEmployee = this.addEmployee.bind(this);
        this.deleteEmployee = this.deleteEmployee.bind(this);
        this.handleLocationTableChanged = this.handleLocationTableChanged.bind(this);
        this.addLocation = this.addLocation.bind(this);
        this.deleteLocation = this.deleteLocation.bind(this);
        this.handlePracticeTableChanged = this.handlePracticeTableChanged.bind(this);
        this.handleCompanyTableChanged = this.handleCompanyTableChanged.bind(this);
        this.handleInputChange = this.handleInputChange.bind(this);

        this.handleSave = this.handleSave.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
    }

    public async componentDidMount() {
        try {
            const startDate = DateTime.local().startOf("week").plus({ day: 6 });
            const [providers, locations, goals, colors] = await Promise.all([
                apiGet<any>(`employees/summary?group=${WellKnownGroups.ProviderStats}`),
                apiGet<any>(`locations?type=office`),
                V2.get<any>(`metrics/case-stats/goals?after=${startDate.toISODate()}`),
                V2.get<any>(`metrics/case-stats/colors`),
            ]);

            const practices = [
                { id: "0", name: "The Howard Center" },
                { id: "1", name: "Tifton Woman's Center PC." }
            ];

            const goalDates = Object.keys(goals);
            const goalData = goalDates.length === 0 ? {} : goals[goalDates[goalDates.length - 1]];

            const employeeGoals: { [id: string]: any } = {};
            const locationGoals: { [id: string]: any } = {};
            const practiceGoals: { [id: string]: any } = {};
            const companyGoals: { [id: string]: any } = {
                "0": {
                    name: "UOG",
                    cpts: {}
                }
            };
            for (const cpt of Object.keys(goalData)) {
                // Employees
                for (const employeeId of Object.keys(goalData[cpt].employees)) {
                    if (!employeeGoals[employeeId]) {
                        employeeGoals[employeeId] = {
                            name: formatName(providers.find((a: any) => a.id === employeeId).name),
                            cpts: {}
                        };
                    }

                    employeeGoals[employeeId].cpts[cpt] = goalData[cpt].employees[employeeId];
                }

                // Locations
                for (const locationId of Object.keys(goalData[cpt].locations)) {
                    if (!locationGoals[locationId]) {
                        locationGoals[locationId] = {
                            name: locations.find((a: any) => a.id === locationId).name,
                            cpts: {}
                        };
                    }

                    locationGoals[locationId].cpts[cpt] = goalData[cpt].locations[locationId];
                }

                // Practices
                // Always show all of them
                for (const prac of practices) {
                    practiceGoals[prac.id] = {
                        name: prac.name,
                        cpts: {}
                    };
                }
                for (const groupId of Object.keys(goalData[cpt].groups)) {
                    if (!practiceGoals[groupId]) {
                        practiceGoals[groupId] = {
                            name: practices.find((a: any) => a.id === groupId)!.name,
                            cpts: {}
                        };
                    }

                    practiceGoals[groupId].cpts[cpt] = goalData[cpt].groups[groupId];
                }

                companyGoals["0"].cpts[cpt] = {
                    dayTarget: goalData[cpt].dayTarget,
                    weekTarget: goalData[cpt].weekTarget,
                    monthTarget: goalData[cpt].monthTarget,
                };
            }

            this.setState({
                isLoading: false,

                goals,
                providers: providers.sort((a: any, b: any) => formatName(a.name).localeCompare(formatName(b.name))),
                locations: locations.sort((a: any, b: any) => a.name.localeCompare(b.name)),
                practices,

                employeeGoals,
                locationGoals,
                practiceGoals,
                companyGoals,

                colorValueGreen: colors[0].value,
                colorValueDullGreen: colors[1].value,
                colorValueYellow: colors[2].value,
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message || e || "Could not load data"
            });
        }
    }

    public render() {
        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.isChanged} message="Your changes will not be saved if you continue." />
                {
                    this.state.isLoading === true
                        ? <div className="spinner spinner-center lds-dual-ring-big" />
                        : typeof this.state.isLoading === "string"
                            ? <span className="error-box">{this.state.isLoading}</span>
                            : (
                                <TabControl headers={["Daily Goals", "Weekly Goals", "Monthly Goals", "Color Ranges"]}>
                                    <div>
                                        {this.renderSection("providers", "daily")}
                                        {this.renderSection("locations", "daily")}
                                        {this.renderSection("practices", "daily")}
                                        {this.renderSection("total", "daily")}
                                    </div>
                                    <div>
                                        {this.renderSection("providers", "weekly")}
                                        {this.renderSection("locations", "weekly")}
                                        {this.renderSection("practices", "weekly")}
                                        {this.renderSection("total", "weekly")}
                                    </div>
                                    <div>
                                        {this.renderSection("providers", "monthly")}
                                        {this.renderSection("locations", "monthly")}
                                        {this.renderSection("practices", "monthly")}
                                        {this.renderSection("total", "monthly")}
                                    </div>
                                    <div>
                                        <h3>Daily</h3>
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>Color</th>
                                                    <th className="col-number">Value</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Bright Green</td>
                                                    <td>&gt;= <input type="number" className="input-small" name="colorValueGreen" value={this.state.colorValueGreen} onChange={this.handleInputChange} /></td>
                                                </tr>
                                                <tr>
                                                    <td>Dull Green</td>
                                                    <td>&gt;= <input type="number" className="input-small" name="colorValueDullGreen" value={this.state.colorValueDullGreen} onChange={this.handleInputChange} /></td>
                                                </tr>
                                                <tr>
                                                    <td>Yellow</td>
                                                    <td>&gt;= <input type="number" className="input-small" name="colorValueYellow" value={this.state.colorValueYellow} onChange={this.handleInputChange} /></td>
                                                </tr>
                                                <tr>
                                                    <td>Red</td>
                                                    <td>&lt;= <input type="number" className="input-small" value={+this.state.colorValueYellow - 1} readOnly={true} /></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </TabControl>
                            )
                }
                &nbsp;
                {
                    this.state.isSubmitting === true
                        ? <div className="spinner lds-dual-ring" />
                        : typeof this.state.isSubmitting === "string"
                            ? <span className="error-text">{this.state.isSubmitting}</span>
                            : undefined
                }
                <div className="action-panel">
                    <button className="button push-right" onClick={this.handleSave} disabled={!!this.state.isLoading || this.state.isSubmitting === true}>Save</button>
                    <button className="button" onClick={this.handleCancel}>Cancel</button>
                </div>
            </div>
        );
    }

    private renderSection(section: "providers" | "locations" | "practices" | "total", kind: "daily" | "weekly" | "monthly") {
        let heading = "UNKNOWN";
        let headingMobile = "UNKNOWN";
        let data: { [id: string]: any } = {};
        let addFunc: ((e: ChangeEvent<HTMLSelectElement>) => void) | undefined = (e: ChangeEvent<HTMLSelectElement>) => { };
        let deleteFunc: ((id: string) => void) | undefined = (id: string) => { };
        let newDataSource: any[] = [];
        let newDataDisplayFunc: (a: any) => any = (item: any) => { };
        let rowChangeHandler = (id: string, cpt: string, value: string, kind: "daily" | "weekly" | "monthly") => { };

        switch (section) {
            case "providers":
                heading = "Providers";
                headingMobile = "Provider Goals";
                data = this.state.employeeGoals;
                addFunc = this.addEmployee;
                deleteFunc = this.deleteEmployee;
                newDataSource = this.state.providers;
                newDataDisplayFunc = (item: any) => formatName(item.name);
                rowChangeHandler = this.handleEmployeeTableChanged;
                break;
            case "locations":
                heading = "Locations";
                headingMobile = "Location Goals";
                data = this.state.locationGoals;
                addFunc = this.addLocation;
                deleteFunc = this.deleteLocation;
                newDataSource = this.state.locations;
                newDataDisplayFunc = (item: any) => item.name;
                rowChangeHandler = this.handleLocationTableChanged;
                break;
            case "practices":
                heading = "Practices";
                headingMobile = "Practice Goals";
                data = this.state.practiceGoals;
                addFunc = undefined;
                deleteFunc = undefined;
                newDataSource = this.state.practices;
                newDataDisplayFunc = (item: any) => item.name;
                rowChangeHandler = this.handlePracticeTableChanged;
                break;
            case "total":
                heading = "Company Total";
                headingMobile = "Company Goals";
                data = this.state.companyGoals;
                addFunc = undefined;
                deleteFunc = undefined;
                newDataSource = [];
                newDataDisplayFunc = (item: any) => item.name;
                rowChangeHandler = this.handleCompanyTableChanged;
                break;
            default: break;
        }

        const getTarget = (a: any) => {
            switch (kind) {
                case "daily": return a.dayTarget;
                case "weekly": return a.weekTarget;
                case "monthly": return a.monthTarget;
                default: return 0;
            }
        };

        return (
            <Fragment>
                <h3>{heading}</h3>
                <table className="table-vertical table-edit nohover">
                    <thead>
                        <tr>
                            <th>Name</th>
                            {this.cpt_list.map(cpt => <th key={cpt} className="col-smallest col-min-4em">{this.getSurgeryName(cpt)}</th>)}
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <thead className="table-vertical-head-small">
                        <tr>
                            <th>{headingMobile}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            Object.keys(data).map(a => (
                                <tr key={a}>
                                    <td className="pad">{data[a].name}</td>
                                    {this.cpt_list.map(cpt => (
                                        <td key={cpt} className="col-smallest col-center" data-caption={this.getSurgeryName(cpt) + ": "}>
                                            <input type="number" min={0} value={getTarget(data[a].cpts[cpt] || {}) || ""} onChange={e => rowChangeHandler(a, cpt, e.currentTarget.value, kind)} />
                                        </td>
                                    ))}
                                    <td><button type="button" className="button red small icon-times" onClick={() => deleteFunc && deleteFunc(a)} disabled={!deleteFunc || newDataSource.length === 0} /></td>
                                </tr>
                            ))
                        }
                        {
                            newDataSource.length > 0 && !!addFunc
                                ? <tr>
                                    <td>
                                        <select onChange={addFunc} value={this.state.newEmployee}>
                                            <option />
                                            {newDataSource.map(a => <option key={a.id} value={a.id} disabled={!!data[a.id]}>{newDataDisplayFunc(a)}</option>)}
                                        </select>
                                    </td>
                                    <td colSpan={7} />
                                </tr>
                                : undefined
                        }
                    </tbody>
                </table>
            </Fragment>
        );
    }

    private getSurgeryName(cpt: string) {
        return ({
            "58558": "D&C",
            "58563": "Ablation",
            "57288": "TVT",
            "58570": "Hysto",
            "58660": "Op lap",
            [TOTALS_CPT]: "Totals"
        } as { [cpt: string]: string })[cpt];
    }

    private addGoalItem(goals: any, id: string, cpt: string) {
        if (!goals[id]) {
            goals[id] = {
                cpts: {}
            };
        }

        if (!goals[id].cpts[cpt]) {
            goals[id].cpts[cpt] = {};
        }
    }

    private handleEmployeeTableChanged(employee: string, cpt: string, value: string, kind: "daily" | "weekly" | "monthly") {
        const goals = this.state.employeeGoals;

        this.addGoalItem(goals, employee, cpt);

        const valueInt = value ? parseInt(value) : undefined;
        switch (kind) {
            case "daily": goals[employee].cpts[cpt].dayTarget = valueInt; break;
            case "weekly": goals[employee].cpts[cpt].weekTarget = valueInt; break;
            case "monthly": goals[employee].cpts[cpt].monthTarget = valueInt; break;
            default: break;
        }

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            employeeGoals: goals
        });
    }

    private addEmployee(e: ChangeEvent<HTMLSelectElement>) {
        const employee = e.currentTarget.value;

        const goals = this.state.employeeGoals;
        if (!goals[employee]) {
            goals[employee] = {
                name: formatName(this.state.providers.find((a: any) => a.id === employee).name),
                cpts: {}
            };
        }

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            employeeGoals: goals,
            newEmployee: ""
        });
    }

    private deleteEmployee(id: string) {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm("This will remove the daily, weekly and monthly goals for this employee.\r\n\r\nDelete?")) {
            return;
        }

        const goals = this.state.employeeGoals;
        delete goals[id];

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            employeeGoals: goals
        });
    }

    private handleLocationTableChanged(location: string, cpt: string, value: string, kind: "daily" | "weekly" | "monthly") {
        const goals = this.state.locationGoals;

        this.addGoalItem(goals, location, cpt);

        const valueInt = value ? parseInt(value) : undefined;
        switch (kind) {
            case "daily": goals[location].cpts[cpt].dayTarget = valueInt; break;
            case "weekly": goals[location].cpts[cpt].weekTarget = valueInt; break;
            case "monthly": goals[location].cpts[cpt].monthTarget = valueInt; break;
            default: break;
        }

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            locationGoals: goals
        });
    }

    private addLocation(e: ChangeEvent<HTMLSelectElement>) {
        const location = e.currentTarget.value;

        const goals = this.state.locationGoals;
        if (!goals[location]) {
            goals[location] = {
                name: this.state.locations.find((a: any) => a.id === location).name,
                cpts: {}
            };
        }

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            locationGoals: goals,
            newEmployee: ""
        });
    }

    private deleteLocation(id: string) {
        // eslint-disable-next-line no-restricted-globals
        if (!confirm("This will remove the daily, weekly and monthly goals for this location.\r\n\r\nDelete?")) {
            return;
        }

        const goals = this.state.locationGoals;
        delete goals[id];

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            locationGoals: goals
        });
    }

    private handlePracticeTableChanged(practice: string, cpt: string, value: string, kind: "daily" | "weekly" | "monthly") {
        const goals = this.state.practiceGoals;

        this.addGoalItem(goals, practice, cpt);

        const valueInt = value ? parseInt(value) : undefined;
        switch (kind) {
            case "daily": goals[practice].cpts[cpt].dayTarget = valueInt; break;
            case "weekly": goals[practice].cpts[cpt].weekTarget = valueInt; break;
            case "monthly": goals[practice].cpts[cpt].monthTarget = valueInt; break;
            default: break;
        }

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            practiceGoals: goals
        });
    }

    private handleCompanyTableChanged(company: string, cpt: string, value: string, kind: "daily" | "weekly" | "monthly") {
        const goals = this.state.companyGoals;

        this.addGoalItem(goals, company, cpt);

        const valueInt = value ? parseInt(value) : undefined;
        switch (kind) {
            case "daily": goals[company].cpts[cpt].dayTarget = valueInt; break;
            case "weekly": goals[company].cpts[cpt].weekTarget = valueInt; break;
            case "monthly": goals[company].cpts[cpt].monthTarget = valueInt; break;
            default: break;
        }

        this.setState({
            isChanged: true,
            isGoalsChanged: true,
            companyGoals: goals
        });
    }

    private async handleSave() {
        const cpts: { [cpt: string]: any } = {};
        const cptArray = [];

        this.setState({
            isSubmitting: true
        });

        if (this.state.isGoalsChanged) {
            // Employees
            for (const employee of Object.keys(this.state.employeeGoals)) {
                const innerCpts = this.state.employeeGoals[employee].cpts;
                for (const cpt of Object.keys(innerCpts)) {
                    if (!innerCpts[cpt].dayTarget && !innerCpts[cpt].weekTarget && !innerCpts[cpt].monthTarget) {
                        continue;
                    }

                    if (!cpts[cpt]) {
                        cpts[cpt] = {
                            cpt,
                            employees: [],
                            groups: [],
                            locations: []
                        };
                    }

                    cpts[cpt].employees.push({
                        employeeId: employee,
                        dayTarget: innerCpts[cpt].dayTarget,
                        weekTarget: innerCpts[cpt].weekTarget,
                        monthTarget: innerCpts[cpt].monthTarget,
                    });
                }
            }

            // Locations
            for (const location of Object.keys(this.state.locationGoals)) {
                const innerCpts = this.state.locationGoals[location].cpts;
                for (const cpt of Object.keys(innerCpts)) {
                    if (!innerCpts[cpt].dayTarget && !innerCpts[cpt].weekTarget && !innerCpts[cpt].monthTarget) {
                        continue;
                    }

                    if (!cpts[cpt]) {
                        cpts[cpt] = {
                            cpt,
                            employees: [],
                            groups: [],
                            locations: []
                        };
                    }

                    cpts[cpt].locations.push({
                        locationId: location,
                        dayTarget: innerCpts[cpt].dayTarget,
                        weekTarget: innerCpts[cpt].weekTarget,
                        monthTarget: innerCpts[cpt].monthTarget,
                    });
                }
            }

            // Practices
            for (const group of Object.keys(this.state.practiceGoals)) {
                const innerCpts = this.state.practiceGoals[group].cpts;
                for (const cpt of Object.keys(innerCpts)) {
                    if (!innerCpts[cpt].dayTarget && !innerCpts[cpt].weekTarget && !innerCpts[cpt].monthTarget) {
                        continue;
                    }

                    if (!cpts[cpt]) {
                        cpts[cpt] = {
                            cpt,
                            employees: [],
                            groups: [],
                            locations: []
                        };
                    }

                    cpts[cpt].groups.push({
                        group: group,
                        dayTarget: innerCpts[cpt].dayTarget,
                        weekTarget: innerCpts[cpt].weekTarget,
                        monthTarget: innerCpts[cpt].monthTarget,
                    });
                }
            }

            // Company
            for (const company of Object.keys(this.state.companyGoals)) {
                const innerCpts = this.state.companyGoals[company].cpts;
                for (const cpt of Object.keys(innerCpts)) {
                    if (!innerCpts[cpt].dayTarget && !innerCpts[cpt].weekTarget && !innerCpts[cpt].monthTarget) {
                        continue;
                    }

                    if (!cpts[cpt]) {
                        cpts[cpt] = {
                            cpt,
                            employees: [],
                            groups: [],
                            locations: []
                        };
                    }

                    Object.assign(cpts[cpt], {
                        dayTarget: innerCpts[cpt].dayTarget,
                        weekTarget: innerCpts[cpt].weekTarget,
                        monthTarget: innerCpts[cpt].monthTarget,
                    });
                }
            }

            for (const key of Object.keys(cpts)) {
                cptArray.push(cpts[key]);
            }
        }

        let colors = [];
        if (this.state.isColorsChanged) {
            colors.push(
                { value: this.state.colorValueGreen, color: "green" },
                { value: this.state.colorValueDullGreen, color: "dullgreen" },
                { value: this.state.colorValueYellow, color: "yellow" },
                { value: this.state.colorValueRed, color: "red" },
            );
        }

        try {
            if (this.state.isGoalsChanged) {
                await V2.put("metrics/case-stats/goals", cptArray);
            }
            if (this.state.isColorsChanged) {
                await V2.put("metrics/case-stats/colors", colors);
            }

            this.setState({
                isChanged: false,
                isColorsChanged: false,
                isGoalsChanged: false,
                isSubmitting: false
            });
            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not save"
            });
        }
    }

    private handleCancel() {
        this.props.history.goBack();
    }

    private handleInputChange(e: ChangeEvent<HTMLInputElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.value;

        this.setState({
            isChanged: true,
            isColorsChanged: true,
            [name]: value
        });
    }
}

export default withTitle("Edit Goals", MetricsCaseStatsGoalEditView);